"use client";

/**
 * stats/page.tsx — 我的进展(四主门之一)· 灯下道场
 *
 * 顶部接住 /train Done 页「记忆成长」叙事:段条语言同款(翡翠=已长/暗毡=未长/
 * 暖金=天后见)+ 最近 8 组正确率 sparkline(末组暖金)。开场是「你的记忆在长」,
 * 不是 KPI 罗列。
 *
 * 信息层级:1 个主视觉 = 牌桌热区(位置×街,放大);趋势/按街/题库/FSRS 弱点
 * 降为次级带状。全页无 <th>/<td>。
 *
 * heatColor 阈值(0.8/0.6/0.4)不动,只把色阶对齐灯下道场语义:
 * 高→暖金 / 良→翡翠 / 平→过渡毡色 / 弱→玫红(成绩观感不变)。
 *
 * 回看页,动效比 /train 更静:一次性轻出场,无答题/牌桌交互。
 */

import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { getFsrsSnapshot, subscribeFsrs } from "@/lib/fsrs";
import type { FsrsCard } from "@/lib/fsrs";
import { normalizePosition } from "@/lib/normalize-position";
import { getProgressSnapshot, subscribeProgress } from "@/lib/progress";
import { positionZh, conceptZh, conceptKeyZh } from "@/lib/poker-terms-zh";
import {
  EMPTY_TRAIN_LOG,
  getTrainLogSnapshot,
  subscribeTrainLog,
  type SessionSummary,
} from "@/lib/train-log";
import {
  EMPTY_RECORD,
  getQuizStats,
  getRecordSnapshot,
  subscribeRecord,
} from "@/lib/quiz-progress";
import type { QuizQuestion } from "@/lib/quiz-types";
import { cn } from "@/lib/cn";
import { DISPLAY_FONT, EASE_OUT } from "@/components/dojo-theme";
import { EmptyGuide } from "@/components/dojo";

type QuizIndexRow = Pick<QuizQuestion, "id" | "category" | "category_name">;

type SceneMeta = {
  scene_id: string;
  street: string;
  hero_position: string;
  concepts?: { scenarios?: string[] };
};

const STREET_LABEL: Record<string, string> = {
  preflop: "翻前",
  flop: "翻牌",
  turn: "转牌",
  river: "河牌",
  multi: "多街",
};

const STREETS = ["preflop", "flop", "turn", "river", "multi"];
const POSITIONS = ["UTG", "MP", "CO", "BTN", "SB", "BB"];

// SSR 服务器快照:服务器无 localStorage,统一空值,与客户端 hydration 首帧对齐
const EMPTY_PROGRESS: ReturnType<typeof getProgressSnapshot> = { answered: {} };
const EMPTY_FSR: ReturnType<typeof getFsrsSnapshot> = {};

/** 热区色阶 — 阈值保持不动(0.8/0.6/0.4),仅换成灯下道场语义色 */
function heatColor(rate: number | null): string {
  if (rate === null) return "bg-[#0E1712]/70 text-stone-600";
  if (rate >= 0.8)
    return "bg-amber-300/20 text-amber-200 shadow-[0_0_18px_-8px_rgba(252,211,77,0.55)]";
  if (rate >= 0.6) return "bg-emerald-400/15 text-emerald-200";
  if (rate >= 0.4) return "bg-[#223329]/90 text-stone-300";
  return "bg-rose-400/15 text-rose-200";
}

/** stability(天)→ 0-5 段:与 Done 页 train-memory-growth 同门槛(同款视觉,不复改其文件) */
const SEG_THRESHOLDS = [0.5, 1.5, 3.5, 8, 16];

function stabilitySegs(s: number): number {
  return SEG_THRESHOLDS.filter((t) => s >= t).length;
}

function daysLabel(ts: number, now: number): string {
  const days = Math.round((ts - now) / 86_400_000);
  return days <= 0 ? "明天" : `${days} 天后再见`;
}

// ─── 道场氛围框(同 /train DojoFrame 语言,灯更静一档) ────────────────────────

function StatsFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative -m-6 min-h-[100vh] overflow-hidden bg-[#090E0B] md:-m-8">
      {/* 环境光:头顶暖灯(更弱)+ 牌桌毡光 + 四周暗角 */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_75%_45%_at_50%_-8%,rgba(228,184,91,0.10),transparent_65%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_62%_50%_at_50%_32%,rgba(34,92,63,0.14),transparent_72%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_130%_100%_at_50%_55%,transparent_55%,rgba(0,0,0,0.5)_100%)]" />
      </div>
      <div className="relative mx-auto w-full max-w-4xl px-5 py-10 md:px-6 md:py-12">
        {children}
      </div>
    </div>
  );
}

// ─── 记忆成长总览(段条 + sparkline,接 Done 页语言) ─────────────────────────

/** 单个概念的记忆段条:已长=翡翠,未长=暗毡,右侧暖金「N 天后再见」(静态版 MemoryBar) */
function MemoryStrip({ card, now }: { card: FsrsCard; now: number }) {
  const segs = stabilitySegs(card.stability);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#1E2E23] bg-[#0D1510]/80 px-4 py-2.5">
      <div className="min-w-0 flex-1 truncate text-sm font-medium text-stone-200">
        {conceptKeyZh(card.key)}
      </div>
      <div className="flex w-24 shrink-0 gap-1 md:w-32" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={cn(
              "h-2.5 flex-1 rounded-full",
              i < segs ? "bg-emerald-400/50" : "bg-[#223329]",
            )}
          />
        ))}
      </div>
      <span
        className={cn(
          DISPLAY_FONT,
          "w-[4.5rem] shrink-0 text-right text-sm font-bold tracking-tight text-amber-300/90",
        )}
      >
        {daysLabel(card.next_review, now)}
      </span>
    </div>
  );
}

/** 最近 N 组正确率 sparkline(柱形,末组暖金 — 与 TodayDojoHero.AccuracySpark 同款) */
function SessionSpark({ sessions, reduce }: { sessions: SessionSummary[]; reduce: boolean }) {
  const pts = sessions.map((s) => (s.total > 0 ? s.correct / s.total : 0));
  const lastPct = Math.round((pts[pts.length - 1] ?? 0) * 100);
  return (
    <div className="relative border-t border-white/5 px-5 pb-4 pt-3.5 md:px-6">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-medium tracking-[0.18em] text-stone-500">
          最近 {sessions.length} 组正确率
        </span>
        <span className="font-mono text-xs tabular-nums text-amber-300/90">{lastPct}%</span>
      </div>
      <div className="mt-2.5 flex h-9 items-end gap-1" aria-hidden>
        {pts.map((p, i) => {
          const isLast = i === pts.length - 1;
          return (
            <motion.span
              key={i}
              title={`${Math.round(p * 100)}%`}
              className={cn(
                "flex-1 origin-bottom rounded-t-[3px]",
                isLast
                  ? "bg-amber-300 shadow-[0_0_12px_rgba(252,211,77,0.35)]"
                  : p >= 0.7
                    ? "bg-emerald-400/70"
                    : p >= 0.4
                      ? "bg-emerald-400/40"
                      : "bg-[#2E4437]",
              )}
              style={{ height: `${Math.max(8, p * 100)}%` }}
              initial={reduce ? false : { scaleY: 0.15, opacity: 0.4 }}
              animate={{ scaleY: 1, opacity: 1 }}
              transition={
                reduce
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 260, damping: 24, delay: 0.25 + i * 0.05 }
              }
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── 次级带状卡(小、紧凑、陪衬) ──────────────────────────────────────────────

function Band({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[#1E2E23] bg-[#0C130F]/70 p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-medium tracking-wider text-stone-500">{title}</h3>
        {hint && (
          <span className="font-mono text-[10px] tabular-nums text-stone-600">{hint}</span>
        )}
      </div>
      {children}
    </section>
  );
}

function LegendDot({ dot, label }: { dot: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn("size-2 rounded-[3px]", dot)} />
      {label}
    </span>
  );
}

// ─── 页面 ─────────────────────────────────────────────────────────────────────

export default function StatsPage() {
  // 客户端独有数据(localStorage)经 useSyncExternalStore + 空服务器快照读取,hydration 安全
  const progress = useSyncExternalStore(
    subscribeProgress,
    getProgressSnapshot,
    () => EMPTY_PROGRESS,
  );
  const fsrs = useSyncExternalStore(
    subscribeFsrs,
    getFsrsSnapshot,
    () => EMPTY_FSR,
  );
  const trainLog = useSyncExternalStore(
    subscribeTrainLog,
    getTrainLogSnapshot,
    () => EMPTY_TRAIN_LOG,
  );
  const [now] = useState(() => Date.now());
  const [sceneMeta, setSceneMeta] = useState<Record<string, SceneMeta>>({});
  const [quizQuestions, setQuizQuestions] = useState<QuizIndexRow[]>([]);
  // 题库记录:与 quiz 页同一真源;server snapshot 为空,hydration 安全
  const quizRecord = useSyncExternalStore(
    subscribeRecord,
    getRecordSnapshot,
    () => EMPTY_RECORD,
  );
  const reduce = useReducedMotion() ?? false;

  useEffect(() => {
    fetch("/api/quiz?mode=index", { cache: "no-store" })
      .then((r) => r.json())
      .then((data: { questions?: QuizIndexRow[] }) => setQuizQuestions(data.questions || []))
      .catch(() => {});
  }, []);

  const quizStats = useMemo(
    () => getQuizStats(quizRecord, quizQuestions.length > 0 ? quizQuestions : undefined),
    [quizRecord, quizQuestions],
  );

  useEffect(() => {
    fetch("/api/scenes?mode=meta", { cache: "no-store" })
      .then((r) => r.json())
      .then((data: { scenes?: Array<{ scene_id: string; spot: { street: string; hero_position: string }; concepts?: { scenarios?: string[] } }> }) => {
        const map: Record<string, SceneMeta> = {};
        for (const s of data.scenes || []) {
          map[s.scene_id] = {
            scene_id: s.scene_id,
            street: s.spot.street,
            hero_position: s.spot.hero_position,
            concepts: s.concepts,
          };
        }
        setSceneMeta(map);
      })
      .catch(() => {});
  }, []);

  const answers = Object.entries(progress.answered);
  const totalAnswered = answers.length;
  const totalCorrect = answers.filter(([, v]) => v.last.correct).length;
  const accuracy = totalAnswered > 0 ? totalCorrect / totalAnswered : 0;

  const fsrsCards = Object.values(fsrs);
  const dueCount = fsrsCards.filter((c) => c.next_review <= now).length;
  const learnedCount = fsrsCards.filter((c) => c.reps >= 3 && c.stability > 7).length;

  // 记忆成长总览:段数最多的前 5 个概念(记忆最强的在前)
  const memoryRows = useMemo(
    () => [...fsrsCards].sort((a, b) => b.stability - a.stability).slice(0, 5),
    [fsrsCards],
  );

  const spark = trainLog.sessions.slice(-8);

  const weakest = [...fsrsCards]
    .filter((c) => c.reps >= 1)
    .sort((a, b) => a.stability - b.stability)
    .slice(0, 8);

  const streetStats = STREETS.map((street) => {
    const entries = answers.filter(([id]) => {
      const meta = sceneMeta[id];
      return meta?.street === street;
    });
    const correct = entries.filter(([, v]) => v.last.correct).length;
    return {
      street,
      total: entries.length,
      correct,
      rate: entries.length > 0 ? correct / entries.length : null,
    };
  });

  // 主视觉热区:rate + n(题数进悬停提示,同一份数据,不是新指标)
  const heatmap = STREETS.map((street) =>
    POSITIONS.map((pos) => {
      const entries = answers.filter(([id]) => {
        const meta = sceneMeta[id];
        if (!meta) return false;
        return meta.street === street && normalizePosition(meta.hero_position) === pos;
      });
      if (entries.length === 0) return null;
      const correct = entries.filter(([, v]) => v.last.correct).length;
      return { rate: correct / entries.length, n: entries.length };
    }),
  );
  const hasHeat = heatmap.some((row) => row.some((c) => c !== null));

  const recentAnswers = [...answers]
    .sort((a, b) => (a[1].last.at ?? 0) - (b[1].last.at ?? 0))
    .slice(-20);

  const trendBuckets = buildTrend(recentAnswers.map(([, v]) => v.last.correct));

  // 回看页的出场:一次性轻浮入,比 /train 更静
  const list: Variants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: reduce ? 0 : 0.07, delayChildren: reduce ? 0 : 0.04 },
    },
  };
  const item: Variants = {
    hidden: { opacity: 0, y: reduce ? 0 : 14 },
    visible: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0.2 } : { duration: 0.45, ease: EASE_OUT },
    },
  };

  return (
    <StatsFrame>
      <motion.div
        className="space-y-6"
        variants={list}
        initial="hidden"
        animate="visible"
      >
        <motion.header variants={item} className="space-y-3">
          <p className="text-[11px] font-semibold tracking-[0.24em] text-amber-300/70">
            GTO DOJO
          </p>
          <h1
            className={cn(
              DISPLAY_FONT,
              "text-4xl font-extrabold tracking-tight text-stone-50 md:text-5xl",
            )}
          >
            我的进展
          </h1>
          <p className="text-sm text-stone-500">记忆成长 · 牌桌热区 · 弱点诊断</p>
        </motion.header>

        {totalAnswered === 0 && fsrsCards.length === 0 && (
          <motion.div variants={item}>
            {/* PLAN #17f5 ①:一行字空态 → ≥3 张真实路由引导卡(去训练/去做题/看概念) */}
            <EmptyGuide
              title="还没有训练数据 — 从任何一扇门开始,练完回来看记忆生长"
              subtitle="三种入门方式,挑一个顺手的;练完一组,这页就会长出记忆条与热区。"
              cards={[
                {
                  href: "/train",
                  title: "去训练",
                  desc: "上桌做第一手决策,牌桌会一步步带你看懂为什么这么打。",
                  cta: "上桌练一组",
                },
                {
                  href: "/quiz",
                  title: "去做题",
                  desc: "牌面题库即做即讲,做错的题可以直接带去问教练。",
                  cta: "开始做题",
                },
                {
                  href: "/lessons",
                  title: "看概念",
                  desc: "先学一课最短入门,再回来把同一手牌打对。",
                  cta: "学第一课",
                },
              ]}
            />
          </motion.div>
        )}

        {(totalAnswered > 0 || fsrsCards.length > 0) && (
          <>
            {/* ── 开场:记忆成长总览(接 Done 页语言) ── */}
            <motion.section
              variants={item}
              className="relative overflow-hidden rounded-2xl border border-[#223329] bg-[#0D1510]/80"
            >
              {/* 灯下光晕:暖金,从左上斜下来 */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_90%_at_15%_-10%,rgba(228,184,91,0.13),transparent_62%)]"
              />

              <div className="relative p-5 md:p-6">
                <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
                  <div>
                    <p className="text-[11px] font-semibold tracking-[0.24em] text-amber-300/70">
                      记忆成长
                    </p>
                    <div className="mt-2 flex items-baseline gap-2.5">
                      <span
                        className={cn(
                          DISPLAY_FONT,
                          "text-5xl font-extrabold leading-[0.9] tracking-tight text-amber-300 md:text-6xl",
                        )}
                      >
                        {learnedCount}
                      </span>
                      <span className="text-sm font-medium text-stone-400">
                        个概念已掌握
                      </span>
                    </div>
                    {fsrsCards.length === 0 && (
                      <p className="mt-2 text-xs text-stone-500">
                        练完一组,这里就会长出记忆条。
                      </p>
                    )}
                  </div>

                  {/* 紧凑数字带:原来的 4 张 KPI 卡收敛成一行 */}
                  <div className="flex gap-5 md:gap-6">
                    <div className="text-right">
                      <div className="font-mono text-lg font-semibold tabular-nums text-stone-100">
                        {totalAnswered}
                      </div>
                      <div className="text-[10px] font-medium tracking-wider text-stone-500">
                        已答题
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={cn(
                          "font-mono text-lg font-semibold tabular-nums",
                          accuracy >= 0.7
                            ? "text-emerald-300"
                            : accuracy >= 0.5
                              ? "text-amber-300"
                              : "text-rose-300",
                        )}
                      >
                        {totalAnswered > 0 ? `${Math.round(accuracy * 100)}%` : "—"}
                      </div>
                      <div className="text-[10px] font-medium tracking-wider text-stone-500">
                        正确率
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={cn(
                          "font-mono text-lg font-semibold tabular-nums",
                          dueCount > 0 ? "text-emerald-300" : "text-stone-100",
                        )}
                      >
                        {dueCount}
                      </div>
                      <div className="text-[10px] font-medium tracking-wider text-stone-500">
                        待复习
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {memoryRows.length > 0 && (
                <div className="relative space-y-2 border-t border-white/5 px-5 py-4 md:px-6">
                  {memoryRows.map((card) => (
                    <MemoryStrip key={card.key} card={card} now={now} />
                  ))}
                </div>
              )}

              {spark.length >= 2 && <SessionSpark sessions={spark} reduce={reduce} />}
            </motion.section>

            {/* ── 主视觉:牌桌热区(位置 × 街) ── */}
            {hasHeat && (
              <motion.section
                variants={item}
                className="relative overflow-hidden rounded-2xl border border-[#2A3D30] bg-[#0D1510]/80 p-5 md:p-6"
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_50%_-12%,rgba(228,184,91,0.07),transparent_60%)]"
                />
                <div className="relative">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h2
                        className={cn(
                          DISPLAY_FONT,
                          "text-xl font-bold tracking-tight text-stone-50",
                        )}
                      >
                        牌桌热区
                      </h2>
                      <p className="mt-1 text-xs text-stone-500">
                        位置 × 街 · 正确率 · 悬停看题数
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-stone-500">
                      <LegendDot dot="bg-amber-300/70" label="≥80% 稳" />
                      <LegendDot dot="bg-emerald-400/60" label="≥60% 良" />
                      <LegendDot dot="bg-[#2A3D30]" label="≥40% 平" />
                      <LegendDot dot="bg-rose-400/60" label="<40% 弱" />
                    </div>
                  </div>

                  <div className="mt-5 overflow-x-auto">
                    <div className="min-w-[520px]">
                      <div className="grid grid-cols-[3rem_repeat(6,minmax(0,1fr))] gap-1.5">
                        <div />
                        {POSITIONS.map((p) => (
                          <div
                            key={p}
                            title={p}
                            className="pb-1 text-center text-xs font-semibold text-stone-500"
                          >
                            {positionZh(p)}
                          </div>
                        ))}
                        {STREETS.map((street, si) => (
                          <Fragment key={street}>
                            <div className="flex items-center justify-end pr-2 text-xs text-stone-400">
                              {STREET_LABEL[street] || street}
                            </div>
                            {POSITIONS.map((pos, pi) => {
                              const cell = heatmap[si][pi];
                              return (
                                <motion.div
                                  key={pos}
                                  title={
                                    cell
                                      ? `${STREET_LABEL[street] || street} · ${positionZh(pos) || pos} · 正确率 ${Math.round(cell.rate * 100)}%(${cell.n} 题)`
                                      : `${STREET_LABEL[street] || street} · ${positionZh(pos) || pos} · 还没练过`
                                  }
                                  className={cn(
                                    "cursor-default rounded-lg py-3 text-center transition-transform duration-150 hover:scale-[1.05]",
                                    heatColor(cell?.rate ?? null),
                                  )}
                                  initial={reduce ? false : { opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={
                                    reduce
                                      ? { duration: 0 }
                                      : {
                                          duration: 0.35,
                                          delay: 0.15 + si * 0.06,
                                          ease: EASE_OUT,
                                        }
                                  }
                                >
                                  <span
                                    className={cn(
                                      DISPLAY_FONT,
                                      "text-sm font-bold tabular-nums md:text-base",
                                    )}
                                  >
                                    {cell ? `${Math.round(cell.rate * 100)}%` : "·"}
                                  </span>
                                </motion.div>
                              );
                            })}
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.section>
            )}

            {/* ── 次级带状:趋势 / 按街 / 题库 / 弱点,紧凑陪衬 ── */}
            <div className="grid gap-3 lg:grid-cols-2">
              {trendBuckets.length > 1 && (
                <motion.div variants={item}>
                  <Band title="正确率趋势" hint="最近 20 题 · 每 5 题一桶">
                    <div className="flex items-end gap-1" style={{ height: 44 }}>
                      {trendBuckets.map((rate, i) => (
                        <div
                          key={i}
                          className={cn(
                            "flex-1 rounded-t",
                            rate >= 0.7
                              ? "bg-emerald-400/60"
                              : rate >= 0.4
                                ? "bg-amber-300/50"
                                : "bg-rose-400/50",
                          )}
                          style={{ height: `${Math.max(rate * 100, 6)}%` }}
                          title={`${Math.round(rate * 100)}%`}
                        />
                      ))}
                    </div>
                  </Band>
                </motion.div>
              )}

              {streetStats.some((s) => s.total > 0) && (
                <motion.div variants={item}>
                  <Band title="按街正确率">
                    <div className="space-y-2">
                      {streetStats
                        .filter((s) => s.total > 0)
                        .map((s) => (
                          <div key={s.street} className="flex items-center gap-3">
                            <span className="w-10 shrink-0 text-xs text-stone-400">
                              {STREET_LABEL[s.street] || s.street}
                            </span>
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#131E17]">
                              <div
                                className={cn(
                                  "h-full rounded-full",
                                  (s.rate ?? 0) >= 0.7
                                    ? "bg-emerald-400"
                                    : (s.rate ?? 0) >= 0.4
                                      ? "bg-amber-300/80"
                                      : "bg-rose-400/80",
                                )}
                                style={{ width: `${(s.rate ?? 0) * 100}%` }}
                              />
                            </div>
                            <span className="w-14 shrink-0 text-right font-mono text-xs tabular-nums text-stone-400">
                              {s.rate !== null ? `${Math.round(s.rate * 100)}%` : "—"}
                              <span className="ml-1 text-stone-600">({s.total})</span>
                            </span>
                          </div>
                        ))}
                    </div>
                  </Band>
                </motion.div>
              )}

              <motion.div variants={item}>
                <Band title="题库表现">
                  {quizStats.answered === 0 ? (
                    <p className="text-xs leading-relaxed text-stone-500">
                      还没有题库作答记录。去{" "}
                      <a
                        href="/quiz"
                        className="text-emerald-300 underline-offset-4 hover:underline"
                      >
                        题库
                      </a>{" "}
                      做几道题吧。
                    </p>
                  ) : (
                    <div className="space-y-3.5">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
                        <span className="text-xs text-stone-500">
                          已作答
                          <b className="ml-1.5 font-mono text-sm font-semibold tabular-nums text-stone-100">
                            {quizStats.answered}
                          </b>
                        </span>
                        <span className="text-xs text-stone-500">
                          答对
                          <b className="ml-1.5 font-mono text-sm font-semibold tabular-nums text-emerald-300">
                            {quizStats.correct}
                          </b>
                        </span>
                        <span className="text-xs text-stone-500">
                          正确率
                          <b
                            className={cn(
                              "ml-1.5 font-mono text-sm font-semibold tabular-nums",
                              (quizStats.accuracy ?? 0) >= 0.7
                                ? "text-emerald-300"
                                : (quizStats.accuracy ?? 0) >= 0.5
                                  ? "text-amber-300"
                                  : "text-rose-300",
                            )}
                          >
                            {quizStats.accuracy !== null
                              ? `${Math.round(quizStats.accuracy * 100)}%`
                              : "—"}
                          </b>
                        </span>
                      </div>
                      {quizStats.byCategory.length > 0 && (
                        <div className="space-y-2">
                          {quizStats.byCategory.map((c) => {
                            const rate = c.correct / c.answered;
                            return (
                              <div key={c.category} className="flex items-center gap-3">
                                <span className="w-24 shrink-0 truncate text-xs text-stone-400">
                                  {c.category_name}
                                </span>
                                <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#131E17]">
                                  <div
                                    className={cn(
                                      "h-full rounded-full",
                                      rate >= 0.7
                                        ? "bg-emerald-400"
                                        : rate >= 0.4
                                          ? "bg-amber-300/80"
                                          : "bg-rose-400/80",
                                    )}
                                    style={{ width: `${rate * 100}%` }}
                                  />
                                </div>
                                <span className="w-14 shrink-0 text-right font-mono text-xs tabular-nums text-stone-400">
                                  {Math.round(rate * 100)}%
                                  <span className="ml-1 text-stone-600">({c.answered})</span>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </Band>
              </motion.div>

              {weakest.length > 0 && (
                <motion.div variants={item}>
                  <Band title="最弱概念" hint="最容易忘 · 点击去练">
                    <div className="space-y-1.5">
                      {weakest.map((card) => {
                        const parts = card.key.split(":");
                        const streetLabel = STREET_LABEL[parts[0]] || parts[0];
                        const posLabel = parts[1] || "";
                        const scenarioLabel = parts[2] || "";
                        const trainHref = `/train?mode=drill&street=${parts[0] || ""}&scenario=${scenarioLabel}&position=${posLabel}`;
                        return (
                          <a
                            key={card.key}
                            href={trainHref}
                            className="flex items-center justify-between rounded-lg bg-[#0E1712]/70 px-3 py-2 ring-1 ring-inset ring-white/[0.03] transition hover:bg-[#13211A] hover:ring-emerald-400/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60"
                          >
                            <span className="truncate text-xs text-stone-300">
                              {streetLabel} · {positionZh(posLabel)} ·{" "}
                              {conceptZh(scenarioLabel) || "通用"}
                            </span>
                            <span className="ml-3 flex shrink-0 items-center gap-2 font-mono text-[11px] tabular-nums text-stone-500">
                              <span>{card.reps}次</span>
                              {card.lapses > 0 && (
                                <span className="text-rose-300/70">{card.lapses}忘</span>
                              )}
                              <span className="text-emerald-300/80">→ 练</span>
                            </span>
                          </a>
                        );
                      })}
                    </div>
                  </Band>
                </motion.div>
              )}
            </div>
          </>
        )}
      </motion.div>
    </StatsFrame>
  );
}

function buildTrend(booleans: boolean[], bucketSize = 5): number[] {
  if (booleans.length < bucketSize) return [];
  const buckets: number[] = [];
  for (let i = 0; i < booleans.length; i += bucketSize) {
    const slice = booleans.slice(i, i + bucketSize);
    if (slice.length === 0) break;
    buckets.push(slice.filter(Boolean).length / slice.length);
  }
  return buckets;
}
